import { durationAs } from '@eturi/date-util'
import { omit } from '@eturi/util'
import type { RawBlockStatus } from '../types/BlockStatus.js'
import type { PruneRawUsageOmitKeys, RawUsage, Usage, UsageDisplayState } from '../types/Usage.js'

const PRUNED_RAW_USAGE_OMIT_KEYS = [
	'device_state',
	'request_ts',
	'usage_date',
] as const satisfies PruneRawUsageOmitKeys[]

const ALL_DAY_SECS = durationAs(1, 'd', 's')

export const mapRawBlockStatusToUsage = (raw: Maybe<RawBlockStatus>): Maybe<Usage> => {
	const rawUsage = raw?.usage

	if (!rawUsage) return null

	const time_limit = rawUsage.time_limit || 0
	// Ensure time_remaining is never negative
	const time_remaining = Math.max(0, rawUsage.time_remaining || 0)
	// iOS Child doesn't always automatically set allowance to 'paused' when limit is reached. This
	// results in `time_used > time_limit`, which screws up calculations. Hopefully the child clients
	// and / or server fix this.
	const time_used = Math.min(time_limit, rawUsage.time_used || 0)

	return {
		...omit(rawUsage, PRUNED_RAW_USAGE_OMIT_KEYS),
		canShowRemaining: time_limit < ALL_DAY_SECS,
		devices_in_use: rawUsage.devices_in_use || [],
		displayState: time_remaining === 0 ? 'expired' : getUsageDisplayState(rawUsage),
		time_limit,
		time_remaining,
		time_used,
	}
}

export const mapRawToUsage = (raw: RawUsage): Usage => {
	const time_limit = raw.time_limit || 0
	const time_remaining = Math.max(0, raw.time_remaining || 0)
	// iOS Child doesn't always automatically set allowance to 'paused' when limit is reached. This
	// results in `time_used > time_limit`, which screws up calculations. Hopefully the child clients
	// and / or server fix this.
	const time_used = Math.min(time_limit, raw.time_used || 0)

	return {
		...omit(raw, PRUNED_RAW_USAGE_OMIT_KEYS),
		canShowRemaining: time_limit < ALL_DAY_SECS,
		devices_in_use: raw.devices_in_use || [],
		displayState: getUsageDisplayState(raw),
		time_limit,
		time_remaining,
		time_used,
	}
}

export const getUsageDisplayState = (raw: RawUsage): UsageDisplayState => {
	// if (isAllowanceBlocked) return 'blocked'

	if (raw.time_remaining === 0) return 'expired'

	const applied = raw.applied_state
	const requested = raw.requested_state

	// if (isAllowOverride) {
	// 	return requested == 'expired' ? requested : 'paused'
	// }

	if (requested == 'play') {
		return applied == 'all' || applied == 'some' ? 'playing' : 'paused'
	}

	if (requested == 'pause') {
		return applied == 'all' || applied == 'some' ? 'paused' : 'playing'
	}

	// // We're optimistic about play states
	// if (requested == 'play') {
	// 	return applied == 'all' || applied == 'some' ? 'playing' : 'paused'
	// }

	// // We're pessimistic about pause states
	// // FIXME: We probably don't need to be this pessimistic. This is UI. Enforcement is server side.
	// if (requested == 'pause') {
	// 	return applied == 'all' ? 'paused' : 'playing'
	// }

	return 'none'
}
